import { Fragment, useEffect, useState, useMemo, CSSProperties, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandToggle from './ExpandToggle';
import styles from './Table.module.scss';
import { withStyles, SubTitle, Icon, Box } from 'vopay';
const cn = withStyles(styles);

export type ColField<Data> = Extract<keyof Data, ID>;

export type ColConfig<Data, ColID extends string> = BaseColConfig<Data, ColID> &
  (FieldColConfig<Data> | CustomColConfig<Data>);

export type Row<Data> = {
  id: ID;
  data: Data;
  subRows?: Row<Data>[];
  presets?: RowPreset[];
};

type BaseColConfig<Data, ColID extends string> = {
  id: ColID;
  style?: CSSProperties;
  visible?: boolean;
  header?: string;
  /**
   * when defining this callback, clicking on this column header will
   * fire this individual sort method
   */
  onSort?: (params: { order: SortMethod<Data, ColID>['order'] }) => void;
  // setting sortable to true will invoke the table-level onSort
  sortable?: boolean;
  onHeaderClick?: () => void;
  align?: 'left' | 'center' | 'right';
  hasDivider?: boolean;
};

type FieldColConfig<Data> = {
  field: ColField<Data>;
  template?: never;
};

type CustomColConfig<Data> = {
  field?: never;
  template: (r: Row<Data>) => React.ReactNode;
};

type RowWithExtra<Data> = {
  id: ID;
  data: Data;
  presets?: RowPreset[];
  expanded: boolean;
  selected: boolean;
  subRows?: RowWithExtra<Data>[];
  currentPage: number;
};

/**
 * This is so that we can enforce the column ids for onSort.
 * That is, the onSort callback function will receive a id that
 * matches the ids defined/provided in the 'cols' prop
 */
type ColumnID<Data, ColID extends string> = ColConfig<Data, ColID>['id'];

type ID = string | number;

type SortMethod<Data, ColID extends string> = {
  id: ColumnID<Data, ColID>;
  order: 'asc' | 'desc';
};

export type RowPreset = 'error' | 'grey' | 'small' | 'blue';

export type TableProps<Data, ColID extends string> = {
  id?: string;
  title?: string;
  rows: Row<Data>[];
  subRowEmptyMessage?: string;
  hasSubRows?: boolean;
  rowPresets?: (row: Row<Data>) => RowPreset[];
  columns: ColConfig<Data, ColID>[];
  selectable?: boolean;
  onSelect?: (rows: Row<Data>[]) => void;
  onSort?: (params: SortMethod<Data, ColID>) => void;
  renderTopControls?: (params: {
    allExpanded: boolean;
    toggleExpandAll: () => void;
    selectedRows: Row<Data>[];
    allSelected: boolean;
    toggleSelectAll: () => void;
  }) => React.ReactNode;
  className?: string;
  zebra?: boolean;
  preset?: 'record' | 'pair';
  hasPlaceholderArrows: boolean;
  style?: {
    '--headerBackground'?: string;
    '--headerColor'?: string;
    '--headerBorderRadius'?: string;
    '--thBorderBottom'?: string;
  };
};

export function createTableProps<Data, ColID extends string>(
  props: Omit<TableProps<Data, ColID>, 'rows' | 'columns'> & {
    rows: Row<Data>[];
    columns: ColConfig<Data, ColID>[];
  },
): TableProps<Data, ColID> {
  return props;
}

export default Table;

function Table<Data, ColID extends string>({
  id,
  title,
  rows,
  hasSubRows = false,
  rowPresets = () => [],
  columns,
  selectable = false,
  onSelect = () => {},
  onSort = () => {},
  renderTopControls,
  className,
  zebra = false,
  preset = 'record',
  hasPlaceholderArrows = false,
  style = {
    '--headerBackground': 'var(--secondaryColor)',
    '--headerColor': 'var(--secondaryTextColor)',
    '--headerBorderRadius': '14px',
    '--thBorderBottom': 'none',
  },
}: TableProps<Data, ColID>) {
  const [data, setData] = useState<RowWithExtra<Data>[]>([]);
  const [sortMethod, setSortMethod] = useState<SortMethod<Data, ColID> | null>(null);
  const allExpanded = useMemo(() => data.every((r) => r.expanded), [data]);
  const allSelected = useMemo(() => data.length > 0 && data.every((r) => r.selected), [data]);
  const ROWS_PER_PAGE = 20;

  /**
   * TODO: handle subRows:
   * - if parent is selected and all subRows selected
   * - if parent is selected and some subRows selected
   * - if parent is NOT selected and some/all subRows selected
   */
  const selectedRows = useMemo(
    () =>
      data
        .filter((r) => r.selected)
        .map((r) => ({
          id: r.id,
          data: r.data,
          subRows: r.subRows?.map((sr) => ({
            id: sr.id,
            data: sr.data,
          })),
        })),
    [data],
  );

  const processRow = (r: Row<Data>, prev: RowWithExtra<Data>[]): RowWithExtra<Data> => {
    const existingRow = prev.find(({ id: rowId }) => rowId === r.id);

    return {
      ...r,
      id: r.id,
      expanded: existingRow ? existingRow.expanded : false,
      selected: existingRow ? existingRow.selected : false,
      currentPage: existingRow ? existingRow.currentPage : 0,
      subRows: r.subRows ? r.subRows.map((sr) => processRow(sr, existingRow?.subRows || [])) : [],
    };
  };

  useEffect(() => {
    setData((prev) => rows.map((r) => processRow(r, prev)));
  }, [rows]);

  useEffect(() => {
    onSelect(selectedRows);
  }, []);

  const toggleExpandAll = () => {
    const toggleAllExpand = (rows: RowWithExtra<Data>[]): RowWithExtra<Data>[] =>
      rows.map((r) => ({
        ...r,
        expanded: !allExpanded,
        subRows: r.subRows ? toggleAllExpand(r.subRows) : r.subRows,
      }));

    setData((prev) => toggleAllExpand(prev));
  };

  function toggleSelectAll() {
    const toggleAllSelect = (rows: RowWithExtra<Data>[]): RowWithExtra<Data>[] =>
      rows.map((r) => ({
        ...r,
        selected: !allSelected,
        subRows: r.subRows ? toggleAllSelect(r.subRows) : r.subRows,
      }));

    const newSelected = toggleAllSelect(data);
    setData(newSelected);
    onSelect(newSelected);
  }

  /**
   * `parentIsDark` is the shade of the surface these rows sit on: the expanded parent row's own
   * background at depth 1, and the enclosing card's shade deeper than that. Rows alternate against
   * it, so each level of nesting flips between light and dark however deep it goes.
   */
  const renderRows = (
    rows: RowWithExtra<Data>[],
    onChange: (rows: RowWithExtra<Data>[]) => void,
    depth: number,
    parentIsDark = false,
    hasTrailingRow = false,
  ) => {
    function handleExpandClick(row: RowWithExtra<Data>) {
      const toggleExpand = (rows: RowWithExtra<Data>[]): RowWithExtra<Data>[] =>
        rows.map((r) => ({
          ...r,
          expanded: r.id === row.id ? !r.expanded : r.expanded,
        }));

      onChange(toggleExpand(rows));
    }

    function handleSelect(selectedId: ID) {
      const toggleSelect = (rows: RowWithExtra<Data>[]): RowWithExtra<Data>[] =>
        rows.map((r) => ({
          ...r,
          selected: r.id === selectedId ? !r.selected : r.selected,
        }));

      onChange(toggleSelect(rows));
    }

    function onChangeRows(changedRows: RowWithExtra<Data>[], parentRowID: ID) {
      const updatedRows = rows.map((r) => {
        if (r.id === parentRowID) {
          return {
            ...r,
            subRows: changedRows,
          };
        }

        return r;
      });

      onChange(updatedRows);
    }

    return rows.map((row, index) => {
      function loadMore() {
        const incrementPage = (rows: RowWithExtra<Data>[]): RowWithExtra<Data>[] =>
          rows.map((r) => ({
            ...r,
            currentPage: r.id === row.id ? r.currentPage + 1 : r.currentPage,
          }));

        onChange(incrementPage(rows));
      }

      // Top-level rows zebra-stripe; a nested card takes the opposite shade of the surface under it.
      const isDark = depth === 0 ? index % 2 === 1 : !parentIsDark;
      const numLoaded = (row.currentPage + 1) * ROWS_PER_PAGE;
      const numRemaining = Math.max(0, (row.subRows?.length ?? 0) - numLoaded);
      // A row drawing its own sub-rows cannot close its card on itself: the sub-rows are drawn
      // inside it (Figma's `Sub Table Area` sits flush under its parent row). Its card is closed by
      // a CardCloseRow emitted after them instead, so the bottom edge lands below the whole group.
      const rendersSubRows = Boolean(row.expanded && row.subRows && row.subRows.length > 0);
      const isLastOfGroup = index === rows.length - 1 && !hasTrailingRow;
      const closesOwnCard = depth >= 1 && isLastOfGroup;

      return (
        <Fragment key={`${row.id}_row`}>
          <RowComponent
            index={index}
            rowId={row.id}
            isDark={isDark}
            parentIsDark={parentIsDark}
            columns={columns}
            row={{ ...row }}
            rowPresets={[...rowPresets({ ...row }), ...(row.presets || [])]}
            expanded={row.expanded}
            onExpandClick={() => handleExpandClick(row)}
            expandable={hasSubRows || (row.subRows || []).length > 0}
            hasSubRowData={Boolean(row.subRows && row.subRows.length > 0)}
            selectable={selectable}
            selected={row.selected}
            depth={depth}
            lastSubRowItem={closesOwnCard && !rendersSubRows}
            firstSubRowItem={depth >= 1 && index === 0}
            hasPlaceholderArrows={hasPlaceholderArrows}
            onSelect={() => handleSelect(row.id)}
          />

          {row.expanded && row.subRows && row.subRows.length > 0 && (
            <>
              {renderRows(
                row.subRows.slice(0, numLoaded),
                (rows) => {
                  onChangeRows(rows, row.id);
                },
                depth + 1,
                isDark,
                numRemaining > 0,
              )}

              {numRemaining > 0 && (
                <LoadMoreRow
                  loadMore={loadMore}
                  columns={columns}
                  depth={depth + 1}
                  isDark={!isDark}
                  parentIsDark={isDark}
                  numLoadingNext={Math.min(numRemaining, ROWS_PER_PAGE)}
                />
              )}

              {closesOwnCard && (
                <CardCloseRow depth={depth} isDark={isDark} parentIsDark={parentIsDark} columns={columns} />
              )}
            </>
          )}
        </Fragment>
      );
    });
  };

  return (
    <>
      {title ? (
        <div className={cn('title')}>
          <SubTitle subTitle={title} variant="small" />
        </div>
      ) : (
        <></>
      )}

      {renderTopControls &&
        renderTopControls({
          allExpanded,
          toggleExpandAll,
          selectedRows,
          allSelected,
          toggleSelectAll,
        })}

      <table
        id={id}
        data-testid={id ? `table-${id}` : undefined}
        style={style}
        className={cn('table', className, 'preset', preset, zebra && 'zebra')}
      >
        {columns.some((c) => !!c.header) && (
          <thead className={cn('header')}>
            <tr>
              {selectable && (
                <th className={cn('toggle')}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
              )}

              {hasSubRows && (
                <th className={cn('toggle')}>
                  <ExpandToggle expanded={allExpanded} onClick={toggleExpandAll} />
                </th>
              )}

              {columns.map((c) => {
                if (c.visible !== undefined && !c.visible) return null;
                const onSortIndividual = c.onSort;
                const onSortGlobal = c.sortable;
                const sortable = onSortIndividual || onSortGlobal;

                return (
                  <th
                    key={`${c.id}-header`}
                    role="button"
                    className={cn(!(c.hasDivider === undefined || c.hasDivider) && 'noDivider')}
                    style={{
                      cursor: sortable || c.onHeaderClick ? 'pointer' : 'default ',
                    }}
                    onClick={() => {
                      c.onHeaderClick && c.onHeaderClick();

                      if (sortable) {
                        const nextOrder = getNextSortOrder();

                        onSortIndividual &&
                          onSortIndividual({
                            order: nextOrder,
                          });

                        setSortMethod({
                          id: c.id,
                          order: nextOrder,
                        });

                        onSort({
                          id: c.id,
                          order: nextOrder,
                        });
                      }

                      function getNextSortOrder(): SortMethod<Data, ColID>['order'] {
                        if (!sortMethod) return 'asc';

                        if (sortMethod.id !== c.id) {
                          return 'asc';
                        }

                        if (sortMethod.order === 'asc') return 'desc';

                        return 'asc';
                      }
                    }}
                  >
                    <Box flx="row" ai="center" jc={c.align === 'left' ? 'fs' : c.align === 'right' ? 'fe' : 'center'}>
                      <div style={{ width: 'max-content', textAlign: 'right' }}>{c.header}</div>

                      {sortable && (
                        <div className={cn('blue')} style={{ position: 'relative', marginLeft: '5px' }}>
                          {(sortMethod?.order === 'asc' || sortMethod?.id !== c.id) && (
                            <Icon icon="angleTop" height="6.5px" width="10px" />
                          )}

                          {(sortMethod?.order !== 'asc' || sortMethod?.id !== c.id) && (
                            <Icon icon="angleDown" height="6.5px" width="10px" />
                          )}
                        </div>
                      )}
                    </Box>
                  </th>
                );
              })}
            </tr>
          </thead>
        )}

        <tbody>{renderRows(data, (rows) => setData(rows), 0)}</tbody>
      </table>
    </>
  );
}

type RowProps<Data, ColID extends string> = {
  row: Row<Data>;
  rowPresets: RowPreset[];
  rowId: ID;
  columns: ColConfig<Data, ColID>[];
  expanded?: boolean;
  expandable: boolean;
  selectable: boolean;
  selected: boolean;
  hasSubRowData: boolean;
  firstSubRowItem: boolean;
  lastSubRowItem: boolean;
  depth: number;
  hasPlaceholderArrows: boolean;
  index: number;
  /** This row's own shade: zebra at depth 0, the nested card's shade below that. */
  isDark: boolean;
  /** Shade of the surface this row sits on, which its card is drawn against. */
  parentIsDark: boolean;
  onExpandClick?: () => void;
  onSelect?: () => void;
};

/**
 * The bottom edge of a card whose last row is expanded. That row cannot carry the edge itself — its
 * sub-rows are drawn inside the card, below it — so without this the card runs on unterminated and
 * merges into whatever follows, which for a zebra-striped row is the same shade of grey.
 */
function CardCloseRow<Data, ColID extends string>({
  depth,
  isDark,
  parentIsDark,
  columns,
}: {
  depth: number;
  isDark: boolean;
  parentIsDark: boolean;
  columns: ColConfig<Data, ColID>[];
}) {
  return (
    <tr
      aria-hidden
      data-testid="cardClose"
      style={{ '--depth': depth }}
      className={cn(
        'row',
        'subRow',
        'cardCloseRow',
        'lastSubRowItem',
        isDark ? 'cardDark' : 'cardLight',
        parentIsDark && 'bandDark',
      )}
    >
      <td className={cn('loadMoreCell')} colSpan={columns.length}>
        <div className={cn('cell')} />
      </td>
    </tr>
  );
}

/**
 * Closes a nested card that has more rows than one page, and says how many the next click adds —
 * a full page, or just the remainder when fewer than a page are left.
 */
function LoadMoreRow<Data, ColID extends string>({
  loadMore,
  columns,
  depth,
  isDark,
  parentIsDark,
  numLoadingNext,
}: {
  loadMore: () => void;
  columns: ColConfig<Data, ColID>[];
  depth: number;
  isDark: boolean;
  parentIsDark: boolean;
  numLoadingNext: number;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Table' });

  return (
    <tr
      style={{ '--depth': depth }}
      className={cn(
        'row',
        'rowPreset',
        'subRow',
        'loadMoreRow',
        'lastSubRowItem',
        isDark ? 'cardDark' : 'cardLight',
        parentIsDark && 'bandDark',
      )}
    >
      <td className={cn('loadMoreCell')} colSpan={columns.length}>
        <div className={cn('cell', 'align', 'left')}>
          <button type="button" data-testid="loadMore" className={cn('loadMoreBtn')} onClick={loadMore}>
            {t('loadMore', { defaultValue: 'Load {{count}} more', count: numLoadingNext })}
          </button>
        </div>
      </td>
    </tr>
  );
}

function RowComponent<Data, ColID extends string>({
  rowId,
  columns,
  row,
  rowPresets,
  expanded = false,
  expandable,
  selectable,
  selected,
  hasSubRowData,
  firstSubRowItem,
  lastSubRowItem,
  depth,
  hasPlaceholderArrows,
  isDark,
  parentIsDark,
  onExpandClick = () => {},
  onSelect = () => {},
}: RowProps<Data, ColID>) {
  return (
    <tr
      // Each nesting level insets by another step; the SCSS multiplies its padding by this.
      style={depth > 0 ? { '--depth': depth } : undefined}
      className={cn(
        'row',
        'rowPreset',
        'rowGroupFirst',
        'rowGroupLast',
        expanded && 'expanded',
        firstSubRowItem && 'firstSubRowItem',
        lastSubRowItem && 'lastSubRowItem',
        // Expanding never repaints the parent — its band simply continues behind the card its
        // sub-rows are drawn in, and that card takes the opposite shade at every level.
        depth !== 0 ? 'subRow' : undefined,
        depth !== 0 && (isDark ? 'cardDark' : 'cardLight'),
        depth !== 0 && parentIsDark && 'bandDark',
        depth === 0 && isDark ? 'grey' : undefined,
        ...rowPresets,
      )}
    >
      <Cells
        row={row}
        columns={columns}
        rowId={rowId}
        depth={depth}
        selectable={selectable}
        selected={selected}
        expandable={expandable}
        hasSubRowData={hasSubRowData}
        expanded={expanded}
        hasPlaceholderArrows={hasPlaceholderArrows}
        onExpandClick={onExpandClick}
        onSelect={onSelect}
      />
    </tr>
  );
}

function Cells<Data, ColID extends string>({
  row,
  columns,
  rowId,
  depth,
  selectable,
  selected,
  expandable,
  hasSubRowData,
  expanded,
  hasPlaceholderArrows,
  onExpandClick,
  onSelect,
}: {
  row: Row<Data>;
  columns: ColConfig<Data, ColID>[];
  rowId: ID;
  depth: number;
  selectable?: boolean;
  selected?: boolean;
  expandable?: boolean;
  hasSubRowData?: boolean;
  expanded: boolean;
  hasPlaceholderArrows: boolean;
  onExpandClick: () => void;
  onSelect: () => void;
}) {
  const cellRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const [heights, setHeights] = useState<number[]>([]);

  useEffect(() => {
    const newHeights = cellRefs.current.map((ref) => {
      if (ref) {
        const style = getComputedStyle(ref);
        const paddingTop = parseFloat(style.paddingTop);
        const paddingBottom = parseFloat(style.paddingBottom);
        const height = ref.offsetHeight;

        return height - paddingTop - paddingBottom;
      }

      return 0;
    });

    setHeights(newHeights);
  }, []);

  return (
    <>
      {columns.map((c, index) => {
        if (c.visible !== undefined && !c.visible) return null;

        return (
          <td
            key={`${c.id}_${rowId}`}
            ref={(el) => {
              cellRefs.current[index] = el;
            }}
          >
            <div
              style={{
                height: heights[index] ? `${heights[index]}px` : 'auto',
                ...c.style,
              }}
              className={cn('cell', 'align', c.align)}
            >
              {/*
                A row with children is expandable at any depth. Only top-level leaves get the
                placeholder — inside a card the design starts their text at the cell padding. The
                wrapper is skipped when it would be empty, since the cell's flex gap would otherwise
                indent the text by a phantom column.
              */}
              {index === 0 && (selectable || hasSubRowData || (depth === 0 && hasPlaceholderArrows)) && (
                <div>
                  {selectable && (
                    <input type="checkbox" checked={selected} onChange={onSelect} className={cn('toggle')} />
                  )}

                  {expandable && hasSubRowData && <ExpandToggle expanded={expanded} onClick={onExpandClick} />}
                  {!hasSubRowData && hasPlaceholderArrows && depth === 0 && <div className={cn('togglePlaceholder')} />}
                </div>
              )}

              <div>{getNode()}</div>
            </div>
          </td>
        );

        function getNode(): React.ReactNode {
          if (isCustomColConfig(c)) {
            return c.template(row);
          }

          if (isFieldColConfig(c)) {
            const node = row.data[c.field];

            if (typeof node === 'string' || typeof node === 'number') {
              return node;
            }

            if (typeof node === 'boolean') {
              return node ? 'true' : 'false';
            }
          }

          return '';
        }
      })}
    </>
  );
}

function isFieldColConfig<Data>(config: FieldColConfig<Data> | CustomColConfig<Data>): config is FieldColConfig<Data> {
  return 'field' in config;
}

function isCustomColConfig<Data>(
  config: FieldColConfig<Data> | CustomColConfig<Data>,
): config is CustomColConfig<Data> {
  return 'template' in config;
}
